import { Badge, Chip, Link, Rating, Tooltip } from '@mui/material';
import {
  InfoOutlined,
  PaymentsOutlined,
  SupportAgentOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import i18n from '@shared/config/i18n';
import { HiddenArray } from '@ui/molecules';
import {
  DataGridColumns,
  DataGridRenderCellParams,
} from '@unicorn-shared/interface/uiKit/DataGrid/DataGrid.types';
import Tehzor from '@img/tehzor.svg';
import { RelatedApplication } from '../../interfaces';
import { getStatusColor } from '../../libs';
import * as Styled from '../../organisms/table/styled';

const { t } = i18n;

export type TicketsTableRow = {
  id: string;
  number: string;
  status: string[];
  isActiveSupport: boolean;
  isIntegrationTehzor: boolean;
  priority: { id: string; title: string };
  created_at: string;
  class: string;
  type: string;
  description: string;
  payment: { id: string; title: string } | null;
  payment_sum: string;
  object: string;
  house: string;
  apartment: string;
  property: string;
  devices: string[];
  planned_date: string;
  actual_end_at: string;
  sla: boolean;
  assignees: string;
  initiator: string;
  initiator_phone: string;
  rate: number | string;
  rate_comment: string;
  formattedTypes: string;
  isTicketPaid: boolean;
  unreadMessages: number;
  source: { id?: string; provider?: { title: string; slug: string } };
  updated_at: string;
  enterprise: string;
  fields: Record<string, unknown>;
  related: RelatedApplication[];
};

export const columns: DataGridColumns<TicketsTableRow> = [
  {
    field: 'number',
    headerName: '№',
    width: 100,
    sortable: true,
    hideable: false,
    renderCell: ({ value, row }: DataGridRenderCellParams<TicketsTableRow, 'number'>) => {
      /* добавление номера заявки если есть провайдер и айди номера */
      const isProvider = row?.source?.id && row?.source?.provider;

      const newVersion = localStorage.getItem('ticketsNewVersion') === 'new';

      if (!newVersion) {
        return (
          <a href={`/tickets/${String(row.id)}`}>
            {isProvider ? `${value}/${row?.source?.id}` : value}
          </a>
        );
      }

      return <Link>{isProvider ? `${value}/${row?.source?.id}` : value}</Link>;
    },
  },
  {
    field: 'status',
    headerName: t('Label.status') ?? '',
    width: 140,
    sortable: false,
    hideable: true,
    renderCell: ({
      value,
      row: { isActiveSupport, isIntegrationTehzor },
    }: DataGridRenderCellParams<TicketsTableRow, 'status'>) => {
      const id = value[0];
      const title = value[1];

      const getChipIcon = () => {
        if (isIntegrationTehzor) {
          return (
            <Tooltip title={t('RequestForTehzorSupport')}>
              <Tehzor />
            </Tooltip>
          );
        }

        if (isActiveSupport) {
          return (
            <Tooltip title={t('RequestForTechnicalSupport')}>
              <SupportAgentOutlined />
            </Tooltip>
          );
        }

        return undefined;
      };

      return (
        <Chip
          icon={getChipIcon()}
          label={title}
          size="small"
          color={getStatusColor(id)}
        />
      );
    },
  },
  {
    field: 'unreadMessages',
    headerName: t('UnreadMessages') ?? '',
    width: 250,
    sortable: false,
    hideable: true,
    align: 'center',
    renderCell: ({
      value,
    }: DataGridRenderCellParams<TicketsTableRow, 'unreadMessages'>) => (
      <Badge
        badgeContent={value}
        color={value && value > 0 ? 'primary' : 'default'}
        showZero
      />
    ),
  },
  {
    field: 'priority',
    headerName: t('Priority') ?? '',
    width: 140,
    sortable: false,
    hideable: true,
    renderCell: ({ value }: DataGridRenderCellParams<TicketsTableRow, 'priority'>) => {
      if (value.id === 'critical') {
        return <Chip label={value.title} size="small" color="error" />;
      }

      return <Chip label={value.title} size="small" variant="outlined" />;
    },
  },
  {
    field: 'created_at',
    headerName: t('DateOfCreation') ?? '',
    width: 200,
    sortable: true,
    hideable: true,
  },
  {
    field: 'updated_at',
    headerName: t('DateOfUpdate') ?? '',
    width: 210,
    sortable: true,
    hideable: true,
  },
  {
    field: 'class',
    headerName: t('RequestClass') ?? '',
    width: 160,
    sortable: false,
    hideable: true,
  },
  {
    field: 'related',
    headerName: t('RelatedApplications') ?? '',
    width: 180,
    sortable: false,
    hideable: true,
    renderCell: ({ value }: DataGridRenderCellParams<TicketsTableRow, 'related'>) => {
      if (!value.length) return null;

      return <HiddenArray>{value.map(({ number }) => number)}</HiddenArray>;
    },
  },
  {
    field: 'type',
    headerName: t('RequestType') ?? '',
    width: 330,
    sortable: false,
    hideable: true,
    renderCell: ({ value, row }: DataGridRenderCellParams<TicketsTableRow, 'type'>) => (
      <Styled.TypeContainer>
        {row.formattedTypes.length > 0 ? (
          <Tooltip title={row.formattedTypes}>
            <div>
              {row.isTicketPaid ? (
                <PaymentsOutlined fontSize="small" color="action" />
              ) : (
                <InfoOutlined fontSize="small" color="action" />
              )}
            </div>
          </Tooltip>
        ) : null}
        <span>{value}</span>
      </Styled.TypeContainer>
    ),
  },
  {
    field: 'description',
    headerName: t('Description') ?? '',
    width: 330,
    sortable: false,
    hideable: true,
  },
  {
    field: 'payment',
    headerName: t('Payment') ?? '',
    width: 130,
    sortable: false,
    hideable: true,
    renderCell: ({ value }: DataGridRenderCellParams<TicketsTableRow, 'payment'>) => {
      if (!value) {
        return null;
      }

      const paid = value.id === 'paid';

      return <Styled.Payment paid={paid}>{value.title}</Styled.Payment>;
    },
  },
  {
    field: 'payment_sum',
    headerName: t('TotalBills') ?? '',
    width: 165,
    sortable: false,
    hideable: true,
    align: 'right',
  },
  {
    field: 'object',
    headerName: t('AnObject') ?? '',
    width: 200,
    sortable: false,
    hideable: true,
  },
  {
    field: 'house',
    headerName: t('building') ?? '',
    width: 300,
    sortable: false,
    hideable: true,
  },
  {
    field: 'apartment',
    headerName: t('Label.flat') ?? '',
    width: 300,
    sortable: false,
    hideable: true,
  },
  {
    field: 'property',
    headerName: t('room') ?? '',
    width: 200,
    sortable: false,
    hideable: true,
  },
  {
    field: 'devices',
    headerName: t('table.equipment') ?? '',
    width: 230,
    sortable: false,
    hideable: true,
    renderCell: ({ value }: DataGridRenderCellParams<TicketsTableRow, 'devices'>) => (
      <HiddenArray>{value}</HiddenArray>
    ),
  },
  {
    field: 'planned_date',
    headerName: t('NormativeDeadline') ?? '',
    width: 315,
    sortable: true,
    hideable: true,
  },
  {
    field: 'actual_end_at',
    headerName: t('ActualDeadline') ?? '',
    width: 290,
    sortable: false,
    hideable: true,
  },
  {
    field: 'sla',
    headerName: t('SLAStandard') ?? '',
    width: 185,
    sortable: true,
    hideable: true,
    renderCell: ({ value }: DataGridRenderCellParams<TicketsTableRow, 'sla'>) => {
      if (!value) {
        return '';
      }

      return (
        <Tooltip title={t('SLAExpired') ?? ''}>
          <WarningAmberOutlined color="error" fontSize="small" />
        </Tooltip>
      );
    },
  },
  {
    field: 'assignees',
    headerName: t('Performer') ?? '',
    width: 250,
    sortable: false,
    hideable: true,
  },
  {
    field: 'initiator',
    headerName: t('Declarant') ?? '',
    width: 250,
    sortable: false,
    hideable: true,
  },
  {
    field: 'initiator_phone',
    headerName: t('DeclarantPhone') ?? '',
    width: 220,
    sortable: false,
    hideable: true,
  },
  {
    field: 'rate',
    headerName: t('Rating') ?? '',
    width: 120,
    sortable: false,
    hideable: true,
    renderCell: ({ value }: DataGridRenderCellParams<TicketsTableRow, 'rate'>) => (
      <Rating max={5} value={typeof value === 'number' ? value : 0} readOnly size="small" />
    ),
  },
  {
    field: 'rate_comment',
    headerName: t('Label.comment') ?? '',
    width: 250,
    sortable: false,
    hideable: true,
  },
  {
    field: 'enterprise',
    headerName: t('company') ?? '',
    width: 250,
    sortable: false,
    hideable: true,
  },
];
